import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * Admin-only management of the automated quick-reply messages.
 * Agents can only USE these in their chats (read-only for them).
 */
const QuickRepliesPanel = () => {
  const [items, setItems] = useState([]);
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quick-replies`);
      setItems(res.data.quick_replies || []);
    } catch (e) {
      console.error('Failed to load quick replies', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const text = newText.trim();
    if (!text) return;
    setSaving(true); setErr('');
    try {
      await axios.post(`${API}/admin/quick-replies`, { text });
      setNewText('');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Αποτυχία προσθήκης.');
    } finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    setSaving(true); setErr('');
    try {
      await axios.put(`${API}/admin/quick-replies/${id}`, { text });
      setEditingId(null); setEditText('');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Αποτυχία αποθήκευσης.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Διαγραφή αυτού του αυτόματου μηνύματος;')) return;
    try {
      await axios.delete(`${API}/admin/quick-replies/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error('Delete failed', e);
    }
  };

  return (
    <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-5 md:p-6" data-testid="quick-replies-panel">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">💬</span>
        <h3 className="text-white font-bold text-lg">Αυτόματα Μηνύματα</h3>
      </div>
      <p className="text-slate-400 text-sm mb-5">Δημιουργήστε, επεξεργαστείτε ή διαγράψτε τα έτοιμα μηνύματα που χρησιμοποιούν οι αντιπρόσωποι στους διαλόγους (αυτόματη μετάφραση στη γλώσσα του επισκέπτη).</p>

      {/* Add new */}
      <div className="mb-6 rounded-lg border border-gold/25 bg-gold/[0.05] p-3">
        <label className="block text-gold/90 text-xs font-semibold mb-2">Νέο αυτόματο μήνυμα</label>
        <textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          rows={2}
          placeholder="Γράψτε το μήνυμα…"
          className="w-full bg-navy border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-gold outline-none resize-y"
          data-testid="quick-reply-new-input"
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={add}
            disabled={saving || !newText.trim()}
            className="text-sm font-semibold text-navy rounded-lg px-4 py-2 disabled:opacity-50 transition hover:opacity-90"
            style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }}
            data-testid="quick-reply-add-btn"
          >
            + Προσθήκη
          </button>
        </div>
      </div>

      {err && <div className="mb-3 text-red-400 text-sm" data-testid="quick-reply-error">{err}</div>}

      {/* List */}
      <div className="space-y-2" data-testid="quick-replies-list">
        {items.length === 0 && <p className="text-slate-500 text-sm text-center py-6">Δεν υπάρχουν αυτόματα μηνύματα ακόμη.</p>}
        {items.map((it, i) => (
          <div key={it.id} className="rounded-lg border border-slate-700/60 bg-navy/60 p-3" data-testid={`quick-reply-item-${i}`}>
            {editingId === it.id ? (
              <div>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={2}
                  className="w-full bg-navy border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-gold outline-none resize-y"
                  data-testid={`quick-reply-edit-input-${i}`}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button type="button" onClick={() => { setEditingId(null); setEditText(''); }} className="text-xs text-slate-400 hover:text-white px-3 py-1.5" data-testid={`quick-reply-cancel-${i}`}>Άκυρο</button>
                  <button type="button" onClick={() => saveEdit(it.id)} disabled={saving} className="text-xs font-semibold text-navy rounded px-3 py-1.5 disabled:opacity-50" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid={`quick-reply-save-${i}`}>Αποθήκευση</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <span className="flex-1 text-slate-200 text-sm leading-snug">{it.text}</span>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <button type="button" onClick={() => { setEditingId(it.id); setEditText(it.text); }} className="text-xs text-gold hover:text-gold/80 px-2 py-1 border border-gold/40 rounded transition" data-testid={`quick-reply-edit-btn-${i}`}>✏️ Edit</button>
                  <button type="button" onClick={() => remove(it.id)} className="text-xs text-red-400 hover:text-red-300 px-2 py-1 transition" data-testid={`quick-reply-delete-btn-${i}`}>🗑️</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickRepliesPanel;
